import { MessageQueueItem, PhoneCall, PhoneCallStatus } from '@mangovoice/types'

/**
 * Shared mock data for testing the Popup/Call Pop functionality.
 * Used by:
 * - NotificationSettings (settings page example button)
 * - PopupPage (browser dev mode via ?dev=true)
 */

export const exampleConnectedCall: PhoneCall = {
  callUuid: 'example-call-123',
  name: 'John Smith',
  number: '+15551234567',
  status: PhoneCallStatus.CONNECTED,
  startTime: new Date(Date.now() - 120000), // Connected 2 minutes ago
  isOnHold: false,
  isMuted: false,
  callSource: 'deskphone',
  direction: 'inbound',
  isExample: true,
  extensionNumber: '101',
  extensionName: 'Main Desk',
  contactInfo: {
    company_id: '123',
    contact_external_id: '12345',
    contact_id: 'contact_67890',
    contact_type: 'patient',
    guarantor_external_id: 'guarantor_54321',
    is_preferred: true,
    last_modified: '2025-01-15T10:30:00.000Z',
    given_name: 'John',
    family_name: 'Smith',
    full_name: 'John Smith',
    preferred_name: 'Johnny',
    name: {
      preferred: 'Johnny',
      full: 'John Smith',
      first: 'John',
      last: 'Smith',
      middle: '',
      prefix: '',
      suffix: '',
    },
    pbx_id: 4293,
    version: 2,
    pbx_uuid: 'pbx-uuid-12345',
    phone_number: '+15551234567',
    phone_type: 'mobile',
    pk: 'pk_contact_67890',
    sk: 'sk_contact_67890',
    sources: [
      {
        id: 'source_001',
        source_manager: 'mango',
        source_name: 'Mango Voice',
      },
    ],
    external_identifiers: [
      {
        id: 'ext_id_001',
        contact_fk_id: 'contact_67890',
        source_fk_id: 'source_001',
        contact_status: 'Active',
        contact_type: 'patient',
        external_id_value: '12345',
        patient_display_id: 'PT-5678',
      },
    ],
    default_location: 'Lehi',
    integration_data: {
      insurance: {
        '9876': {
          insurance_name: 'Metlife',
        },
      },
      balance: {
        '1234': {
          balance: 0,
        },
      },
      appointment: {
        '2345': {
          start_time: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 7 days from now
          end_time: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'confirmed',
        },
        '3456': {
          start_time: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 30 days from now
          end_time: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'confirmed',
        },
        '4567': {
          start_time: new Date(
            Date.now() + 60 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 60 days from now
          end_time: new Date(
            Date.now() + 60 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'pending',
        },
        '5678': {
          start_time: new Date(
            Date.now() - 90 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 90 days ago
          end_time: new Date(
            Date.now() - 90 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'completed',
        },
      },
    },
  },
}

export const exampleRingingCall: PhoneCall = {
  callUuid: 'example-call-456',
  name: 'Sarah Johnson',
  number: '+15559876543',
  status: PhoneCallStatus.RINGING,
  startTime: new Date(Date.now() - 15000), // Started ringing 15 seconds ago
  isOnHold: false,
  isMuted: false,
  callSource: 'softphone',
  direction: 'inbound',
  isExample: true,
  extensionName: 'Web Phone',
  contactInfo: {
    company_id: '123',
    contact_external_id: '67890',
    contact_id: 'contact_98765',
    contact_type: 'patient',
    guarantor_external_id: 'guarantor_98765',
    is_preferred: true,
    last_modified: '2025-01-15T11:00:00.000Z',
    given_name: 'Sara',
    family_name: 'Johnson',
    full_name: 'Sara Marie Johnson',
    middle_name: 'Marie',
    prefix: 'Ms.',
    name: {
      full: 'Sara Marie Johnson',
      first: 'Sara',
      last: 'Johnson',
      middle: 'Marie',
      prefix: 'Ms.',
      suffix: '',
    },
    pbx_id: 4293,
    pbx_uuid: 'pbx-uuid-67890',
    version: 2,
    phone_number: '+15559876543',
    phone_type: 'home',
    pk: 'pk_contact_98765',
    sk: 'sk_contact_98765',
    sources: [
      {
        id: 'source_002',
        source_manager: 'mango',
        source_name: 'Mango Voice',
      },
    ],
    external_identifiers: [
      {
        id: 'ext_id_002',
        contact_fk_id: 'contact_98765',
        source_fk_id: 'source_002',
        contact_status: 'Inactive',
        contact_type: 'patient',
        external_id_value: '67890',
      },
    ],
    default_location: 'St. George',
    integration_data: {
      insurance: {
        '4567': {
          insurance_name: 'Blue Cross Blue Shield',
        },
      },
      balance: {
        '5678': {
          balance: '125.00',
        },
      },
      appointment: {
        '6789': {
          start_time: new Date(
            Date.now() + 14 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 14 days from now
          end_time: new Date(
            Date.now() + 14 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'confirmed',
        },
        '7890': {
          start_time: new Date(
            Date.now() + 45 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 45 days from now
          end_time: new Date(
            Date.now() + 45 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'pending',
        },
        '8901': {
          start_time: new Date(
            Date.now() + 90 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 90 days from now
          end_time: new Date(
            Date.now() + 90 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'pending',
        },
        '9012': {
          start_time: new Date(
            Date.now() - 120 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 120 days ago
          end_time: new Date(
            Date.now() - 120 * 24 * 60 * 60 * 1000 + 30 * 60 * 1000,
          ).toISOString(),
          status: 'completed',
        },
      },
    },
  },
}

export const exampleTexts: MessageQueueItem[] = [
  {
    id: 1569492,
    message_group_id: 1747,
    name: 'Sarah Johnson',
    external_phone_number: '+15555551234',
    unread_message_count: 2,
    latest_message: {
      id: 11994102,
      pbx_id: 4293,
      message_thread_id: 1569492,
      sender_user_id: undefined,
      direction: 'inbound',
      body: 'Hi, I need to reschedule my appointment for tomorrow. Can we move it to Friday instead?',
      files: [],
      created_at: new Date(Date.now() - 21 * 60 * 1000).toISOString(), // 21 minutes ago
      updated_at: new Date(Date.now() - 21 * 60 * 1000).toISOString(),
    },
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
    updated_at: new Date(Date.now() - 21 * 60 * 1000).toISOString(),
    _id: '11994102', // Use latest_message.id for consistency
    type: 'message',
  },
  {
    id: 1569493,
    message_group_id: 1748,
    name: 'Mike Wilson',
    external_phone_number: '+15555555678',
    unread_message_count: 1,
    latest_message: {
      id: 11994103,
      pbx_id: 4293,
      message_thread_id: 1569493,
      sender_user_id: undefined,
      direction: 'inbound',
      body: 'Thank you for the great service today!',
      files: [],
      created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
      updated_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
    },
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    _id: '11994103', // Use latest_message.id for consistency
    type: 'message',
  },
  {
    id: 1569491,
    message_group_id: 1747,
    name: 'Mango User',
    external_phone_number: '+15555555556',
    unread_message_count: 3,
    latest_message: {
      id: 11994101,
      pbx_id: 4293,
      message_thread_id: 1569491,
      sender_user_id: undefined,
      direction: 'inbound',
      body: 'This is a test text message',
      files: [],
      created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
      updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    updated_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    _id: '11994101', // Use latest_message.id for consistency
    type: 'message',
  },
]

export const exampleCalls = [exampleConnectedCall, exampleRingingCall]

/**
 * All mock data in one object for convenience
 */
export const popupMockData = {
  calls: exampleCalls,
  messages: exampleTexts,
  connectedCall: exampleConnectedCall,
  ringingCall: exampleRingingCall,
}
